import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components/macro";

import { Helmet } from "react-helmet-async";

import {
	Breadcrumbs as MuiBreadcrumbs,
	Button,
	Card as MuiCard,
	CardContent,
	CircularProgress,
	Divider as MuiDivider,
	Grid,
	IconButton,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";

import { spacing } from "@material-ui/system";
import { format, isAfter, isBefore } from "date-fns";
import { useStyles } from "../../theme/useStyles";
import { firestore } from "../../firebase/firebase";
import { Observacion } from "../../models/Observacion";
import { useTable } from "../../hooks/useTable";
import { RangeDate } from "./Filters";
import { generateLegacyExcel } from "../../redux/actions/observacionesActions";
import { getBusiness } from "../../redux/actions/businessActions";
import { LegacyFilters } from "./LegacyFilters";
import { TABLE_LIMITS } from "../../constants";

import { HelperIcon } from "./HelperIcon";
import {
	LinkEmpresas,
	LinkProyectos,
} from "../CheckPro/Links";
import { useBusiness } from "src/hooks/redux/useBusiness";
import { useDispatch, useSelector } from "src/redux/store";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const enumerateObservations = async (
	arr: Observacion[]
): Promise<Observacion[]> => {
	const groupedByListado = arr.reduce((acc, obs) => {
		const listado = obs.NombreListado;
		if (!acc[listado]) {
			acc[listado] = [];
		}
		acc[listado].push(obs);
		return acc;
	}, {} as { [key: string]: Observacion[] });

	return Object.values(groupedByListado)
		.map((listadoArray) =>
			listadoArray.map((o, i) => ({ ...o, Numero: i + 1 }))
		)
		.flat();
};

const ContentCard = () => {
	const classes = useStyles();
	const { getExcel } = useSelector(
		(state) => state.observacionesChecklistReducer
	);
	const { projectId } = useParams() as any;
	const dispatch = useDispatch();
	// Ya no usamos useBusiness para evitar conflictos y parpadeos
	
	// Debug Redux state directly
	const reduxState = useSelector((state: any) => state);
	
	// Ya no necesitamos cargar la empresa aquí porque la acción generateLegacyExcel 
	// la obtendrá directamente del usuario si no está disponible
	

	const [observaciones, setObservaciones] = useState<
		Observacion[]
	>([]);

	const [isLoadingObservations, setIsLoadingObservations] = useState(false);
	const [isLoadingCount, setIsLoadingCount] = useState(true);
	const [currentPage, setCurrentPage] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [availableCount, setAvailableCount] = useState(0);
	const [firstDoc, setFirstDoc] = useState<any>(null);
	const [lastDoc, setLastDoc] = useState<any>(null);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [hasPrevPage, setHasPrevPage] = useState(false);

	// Stack para navegación hacia atrás
	const [docsStack, setDocsStack] = useState<any[]>([]);

	// Función para obtener el conteo total en paralelo
	const fetchTotalCount = async () => {
		setIsLoadingCount(true);
		try {
			const countQuery = firestore
				.collectionGroup("Observaciones")
				.where("ProyectoId", "==", projectId);
			
			const countSnap = await countQuery.get();
			setTotalCount(countSnap.size);
		} catch (error) {
			console.error("Error fetching count:", error);
			setTotalCount(0);
		} finally {
			setIsLoadingCount(false);
		}
	};

	// Conseguir observaciones con paginación
	const fetchObservations = async (direction: 'first' | 'next' | 'prev' = 'first') => {
		setIsLoadingObservations(true);
		
		// Solo mostrar el loader principal si no hay datos
		const shouldShowMainLoader = direction === 'first' && observaciones.length === 0;
		
		try {
			let query = firestore
				.collectionGroup("Observaciones")
				.where("ProyectoId", "==", projectId)
				.orderBy("CreatedAt", "asc")
				.limit(limit + 1); // +1 para saber si hay más páginas

			if (direction === 'next' && lastDoc) {
				query = query.startAfter(lastDoc);
			} else if (direction === 'prev' && docsStack.length > 0) {
				const prevDoc = docsStack[docsStack.length - 1];
				query = query.startAt(prevDoc).limit(limit + 1);
			}

			const snap = await query.get();
			const docs = snap.docs;

			// Determinar si hay más páginas
			const hasMore = docs.length > limit;
			const actualDocs = hasMore ? docs.slice(0, limit) : docs;

			const newObservations = actualDocs.map((doc) => doc.data() as Observacion);
			const enumeratedObservations = await enumerateObservations(newObservations);

			setObservaciones(enumeratedObservations);

			if (actualDocs.length > 0) {
				setFirstDoc(actualDocs[0]);
				setLastDoc(actualDocs[actualDocs.length - 1]);
			}

			// Actualizar navegación
			if (direction === 'next') {
				setCurrentPage(prev => prev + 1);
				setDocsStack(prev => [...prev, firstDoc]);
				setHasPrevPage(true);
			} else if (direction === 'prev') {
				setCurrentPage(prev => prev - 1);
				const newStack = docsStack.slice(0, -1);
				setDocsStack(newStack);
				setHasPrevPage(newStack.length > 0);
			} else {
				setCurrentPage(0);
				setDocsStack([]);
				setHasPrevPage(false);
			}

			setHasNextPage(hasMore);

			// Obtener conteo total aproximado (solo en primera carga)
			if (direction === 'first') {
				await fetchTotalCount();
			}

		} catch (error) {
			console.error("Error fetching observations:", error);
		} finally {
			setIsLoadingObservations(false);
		}
	};

	const {
		limit,
		handleLimitChange,
	} = useTable({
		limit: 20,
	});

	useEffect(() => {
		fetchObservations('first');
	}, [projectId, limit]);

	const handleNextPage = () => {
		if (hasNextPage && !isLoadingObservations) {
			fetchObservations('next');
		}
	};

	const handlePrevPage = () => {
		if (hasPrevPage && !isLoadingObservations) {
			fetchObservations('prev');
		}
	};

	const handleLimitChangeCustom = (event: React.ChangeEvent<HTMLInputElement>) => {
		handleLimitChange(event);
		// Reiniciar a primera página cuando cambie el límite
		setCurrentPage(0);
		setDocsStack([]);
		setHasPrevPage(false);
		fetchObservations('first');
	};
	const [filterData, setFilterData] = useState({});

	const filteredObservations = observaciones.filter(
		(obs) => {
			const arrayOfTests: [string, string | RangeDate][] =
				Object.entries(filterData);
			if (arrayOfTests.length === 0) return true;

			return arrayOfTests
				.map((arrayOfTestsEntry) => {
					const key = arrayOfTestsEntry[0];
					const value = arrayOfTestsEntry[1];
					if (typeof value === "string") {
						return (obs as any)[key] === value;
					} else {
						return (
							isAfter(obs.CreatedAt.toDate(), value[0]) &&
							isBefore(obs.CreatedAt.toDate(), value[1])
						);
					}
				})
				.every((e) => e);
		}
	);

	// Actualizar el contador de registros disponibles cuando cambien los filtros
	useEffect(() => {
		setAvailableCount(filteredObservations.length);
	}, [filteredObservations]);

	const handleGenerateExcel = () => {
		console.log("Generating Excel with:", { projectId, filterData, observaciones: observaciones.length });
		const projectName = observaciones.length > 0 ? observaciones[0]?.NombreProyecto : undefined;
		console.log("About to dispatch generateLegacyExcel with:", { projectId, filterData, projectName });
		try {
			dispatch(generateLegacyExcel(projectId, filterData, projectName));
			console.log("Dispatch executed successfully");
		} catch (error) {
			console.error("Error dispatching generateLegacyExcel:", error);
		}
	};

	return (
		<Card mb={6}>
			<LegacyFilters
				observations={observaciones}
				onChangeFilter={(value, filterName) => {
					const actualFilters: any = { ...filterData };
					if (value === "todo") {
						delete actualFilters[filterName];
					} else {
						actualFilters[filterName] = value;
					}
					setFilterData(actualFilters);
				}}
				onClickGenerateExcel={handleGenerateExcel}
				isLoadingExcel={getExcel.state === "IsSubmitting"}
			/>
			<CardContent>
				{(isLoadingObservations && observaciones.length === 0) || isLoadingCount ? (
					<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
						<CircularProgress />
						<Typography variant="body2" style={{ marginLeft: '12px', alignSelf: 'center' }}>
							{isLoadingObservations ? 'Cargando observaciones...' : 'Calculando total...'}
						</Typography>
					</div>
				) : (
					<>
						<TableContainer className={classes.tableContainer}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Revisión</TableCell>
										<TableCell>Listado</TableCell>
										<TableCell>Sector</TableCell>
										<TableCell>Recinto</TableCell>
										<TableCell>Subcontrato</TableCell>
										<TableCell>Error tipo</TableCell>
										<TableCell>Prioridad</TableCell>
										<TableCell>Orden</TableCell>
										<TableCell>Estado</TableCell>
										<TableCell>Comentario</TableCell>
										{/* <TableCell>Foto</TableCell> */}
										<TableCell>Observada</TableCell>
										<TableCell>Vence</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredObservations
										.map((arr, i) => (
											<TableRow
												key={`obs-${i}`}
												hover
												className={classes.styledRow}
											>
													<TableCell>
														{arr.NombreRevision}
													</TableCell>
													<TableCell>
														{arr.NombreListado}
													</TableCell>
													<TableCell
														style={{ whiteSpace: "nowrap" }}
													>
														{arr.Sector}
													</TableCell>
													<TableCell
														style={{ whiteSpace: "nowrap" }}
													>
														{arr.Recinto}
													</TableCell>
													<TableCell>
														{arr.Subcontrato}
													</TableCell>
													<TableCell>{arr.ErrorTipo}</TableCell>
													<TableCell>{arr.Prioridad}</TableCell>
													<TableCell>
														{arr.Numero || "-"}
													</TableCell>
													<TableCell
														style={{ whiteSpace: "nowrap" }}
													>
														{arr.Estado === "Vencida"
															? "Por Resolver"
															: arr.Estado}
													</TableCell>
													<TableCell
														align="center"
														style={{
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
															maxWidth: 150,
														}}
													>
														<HelperIcon
															title={arr.Comentario}
														/>
													</TableCell>
													{/* <TableCell>
														<CeldaImagen observacion={arr} />
													</TableCell> */}
													<TableCell>
														{format(
															arr.CreatedAt.toDate(),
															"dd/MM/yyyy"
														)}
													</TableCell>
													<TableCell>
														{arr.Vencida
															? format(
																	arr.Vencida.toDate(),
																	"dd/MM/yyyy"
															  )
															: "-"}
													</TableCell>
												</TableRow>
										))}

									{filteredObservations.length === 0 && !isLoadingObservations ? (
										<TableRow>
											<TableCell align={"center"} colSpan={12}>
												<img
													alt="Nada imagen"
													src="/static/no_data.svg"
													height="100"
												/>
												<Typography variant="h5">
													No hay observaciones
												</Typography>
											</TableCell>
										</TableRow>
									) : null}
								</TableBody>
							</Table>
						</TableContainer>
						
						{/* Paginador personalizado */}
						<div style={{ 
							display: 'flex', 
							justifyContent: 'space-between', 
							alignItems: 'center', 
							padding: '16px',
							borderTop: '1px solid #e0e0e0'
						}}>
							<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
								<div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
									<Typography variant="body2" color="textSecondary">
										{availableCount > 0 
											? `Mostrando ${Math.min(filteredObservations.length, limit)} de ${availableCount} registros disponibles`
											: 'No hay registros disponibles'
										}
									</Typography>
									<Typography variant="caption" color="textSecondary">
										Total en proyecto: {totalCount} observaciones
									</Typography>
								</div>
								
								<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									<Typography variant="body2" color="textSecondary">
										Filas por página:
									</Typography>
									<Select
										value={limit}
										onChange={handleLimitChangeCustom}
										variant="outlined"
										style={{ minWidth: '60px', fontSize: '14px' }}
									>
										<MenuItem value={10}>10</MenuItem>
										<MenuItem value={20}>20</MenuItem>
										<MenuItem value={50}>50</MenuItem>
										<MenuItem value={100}>100</MenuItem>
									</Select>
								</div>
							</div>
							
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Tooltip title="Página anterior">
									<span>
										<IconButton
											onClick={handlePrevPage}
											disabled={!hasPrevPage || isLoadingObservations}
											size="small"
										>
											<ChevronLeft />
										</IconButton>
									</span>
								</Tooltip>
								
								<Typography variant="body2" style={{ minWidth: '60px', textAlign: 'center' }}>
									Página {currentPage + 1}
								</Typography>
								
								<Tooltip title="Página siguiente">
									<span>
										<IconButton
											onClick={handleNextPage}
											disabled={!hasNextPage || isLoadingObservations}
											size="small"
										>
											<ChevronRight />
										</IconButton>
									</span>
								</Tooltip>
							</div>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
};

export const RevisionObservationsExcelPage = () => {
	// Obtener business directamente del estado Redux sin hooks adicionales
	const selectedBusiness = useSelector((state: any) => state.businessReducer.edit.selectedBusiness);
	
	const { selectedRevision } = useSelector(
		(s) => s.revisionsReducer
	) as { selectedRevision: any };

	return (
		<>
			<Helmet title="Observaciones" />
			<Breadcrumbs>
				<Typography
					variant="h3"
					gutterBottom
					display="inline"
				>
					{selectedBusiness?.Nombre ?? "Empresa"}
				</Typography>
				<Typography
					variant="h3"
					gutterBottom
					display="inline"
				>
					{selectedRevision?.NombreProyecto ?? "Proyecto"}
				</Typography>
				<Typography
					variant="h3"
					gutterBottom
					display="inline"
				>
					Observaciones
				</Typography>
			</Breadcrumbs>

			<Breadcrumbs aria-label="Breadcrumb" mt={2}>
				<LinkEmpresas />
				<LinkProyectos />
				<Typography>Observaciones</Typography>
			</Breadcrumbs>

			<Divider my={6} />

			<Grid container spacing={6}>
				<Grid item xs={12}>
					<ContentCard />
				</Grid>
			</Grid>
		</>
	);
};
